"use client"

import { useState, useEffect, useMemo } from "react"
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Stack,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Rating,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material"
import {
  Assessment,
  People,
  Assignment,
  Feedback,
  Timeline,
  AccountTree,
  Star,
  CheckCircle,
  Warning,
  PieChart,
  Refresh,
  Description,
  EmojiEvents,
  Speed,
  ShowChart,
  Dashboard,
} from "@mui/icons-material"
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf"

import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  LineChart,
  Line,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
} from "recharts"

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// ✅ Updated API helper with authentication
const api = {
  baseURL: API_BASE_URL,

  // Get token from localStorage
  getToken() {
    return localStorage.getItem('token');
  },

  // Get company_id from token
  getCompanyId() {
    try {
      const token = this.getToken();
      if (!token) return null;
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.company_id;
    } catch (error) {
      //console.error('Error parsing token:', error);
      return null;
    }
  },

  // Get user info from token
  getUserInfo() {
    try {
      const token = this.getToken();
      if (!token) return null;
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload;
    } catch (error) {
      //console.error('Error parsing token:', error);
      return null;
    }
  },

  // Get headers with authentication
  getHeaders() {
    const token = this.getToken();
    return {
      'Authorization': token ? `Bearer ${token}` : '',
      'Content-Type': 'application/json',
    };
  },

  async get(endpoint, options = {}) {
    try {
      const token = this.getToken();
      if (!token) {
        console.warn('⚠️ No authentication token found');
        throw new Error('No authentication token found. Please log in.');
      }

      const url = `${this.baseURL}${endpoint}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          ...this.getHeaders(),
          ...options.headers,
        },
        credentials: 'include', // ✅ Include credentials for session
      });

      if (!response.ok) {
        if (response.status === 401) {
          //console.error('❌ Unauthorized - token may be expired');
          localStorage.removeItem('token');
          window.location.href = '/login';
          throw new Error('Session expired. Please log in again.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      //console.error(`❌ API Error for ${endpoint}:`, error);
      throw error;
    }
  },

  async post(endpoint, body, options = {}) {
    try {
      const token = this.getToken();
      if (!token) {
        console.warn('⚠️ No authentication token found');
        throw new Error('No authentication token found. Please log in.');
      }

      const url = `${this.baseURL}${endpoint}`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...this.getHeaders(),
          ...options.headers,
        },
        body: JSON.stringify(body),
        credentials: 'include', // ✅ Include credentials for session
      });

      if (!response.ok) {
        if (response.status === 401) {
          //console.error('❌ Unauthorized - token may be expired');
          localStorage.removeItem('token');
          window.location.href = '/login';
          throw new Error('Session expired. Please log in again.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      //console.error(`❌ API Error for POST ${endpoint}:`, error);
      throw error;
    }
  },

  async put(endpoint, body, options = {}) {
    try {
      const token = this.getToken();
      if (!token) {
        console.warn('⚠️ No authentication token found');
        throw new Error('No authentication token found. Please log in.');
      }

      const url = `${this.baseURL}${endpoint}`;

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          ...this.getHeaders(),
          ...options.headers,
        },
        body: JSON.stringify(body),
        credentials: 'include', // ✅ Include credentials for session
      });

      if (!response.ok) {
        if (response.status === 401) {
          //console.error('❌ Unauthorized - token may be expired');
          localStorage.removeItem('token');
          window.location.href = '/login';
          throw new Error('Session expired. Please log in again.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      //console.error(`❌ API Error for PUT ${endpoint}:`, error);
      throw error;
    }
  },

  async delete(endpoint, options = {}) {
    try {
      const token = this.getToken();
      if (!token) {
        console.warn('⚠️ No authentication token found');
        throw new Error('No authentication token found. Please log in.');
      }

      const url = `${this.baseURL}${endpoint}`;

      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          ...this.getHeaders(),
          ...options.headers,
        },
        credentials: 'include', // ✅ Include credentials for session
      });

      if (!response.ok) {
        if (response.status === 401) {
          //console.error('❌ Unauthorized - token may be expired');
          localStorage.removeItem('token');
          window.location.href = '/login';
          throw new Error('Session expired. Please log in again.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      //console.error(`❌ API Error for DELETE ${endpoint}:`, error);
      throw error;
    }
  }
};

const CHART_COLORS = ["#1976d2", "#388e3c", "#f57c00", "#d32f2f", "#7b1fa2", "#00796b"]

function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} id={`tabpanel-${index}`} aria-labelledby={`tab-${index}`} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  )
}

const PerformanceReportDashboard = () => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [activeTab, setActiveTab] = useState(0)
  const [timePeriod, setTimePeriod] = useState("all")
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())
  const [availableYears, setAvailableYears] = useState([new Date().getFullYear()])
  const [managerEmployeeId, setManagerEmployeeId] = useState(null)
  const [data, setData] = useState({
    goals: [],
    reviews: [],
    feedback: [],
    improvements: [],
    succession: [],
    employees: [],
    training: [],
  })
  const [selectedGoalStatus, setSelectedGoalStatus] = useState(null)
  const [statusModalOpen, setStatusModalOpen] = useState(false)
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  // Fetch logged-in user's employee_id (manager ID)
  const fetchManagerId = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return null;
      const payload = JSON.parse(atob(token.split(".")[1]));
      const company_id = payload.company_id;
      const response = await fetch(`${API_BASE_URL}/api/user/current?company_id=${company_id}`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });
      if (response.ok) {
        const userData = await response.json();
        if (userData && userData.employee_id) {
          setManagerEmployeeId(userData.employee_id);
          return userData.employee_id;
        }
      }
      return null;
    } catch (err) {
      //console.error("Error fetching current user:", err);
      return null;
    }
  };

  // ✅ Check authentication on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('No authentication token found. Please log in.');
      setLoading(false);
      // Redirect to login after 3 seconds
      setTimeout(() => {
        window.location.href = '/login';
      }, 3000);
      return;
    }

    // Validate token
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (!payload.company_id) {
        throw new Error('Invalid token: No company ID found');
      }
      setIsAuthenticated(true);
    } catch (error) {
      //console.error('Invalid token:', error);
      localStorage.removeItem('token');
      setError('Invalid authentication token. Please log in again.');
      setLoading(false);
      setTimeout(() => {
        window.location.href = '/login';
      }, 3000);
    }
  }, []);

  // Fetch available years
  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchYears = async () => {
      try {
        const response = await api.get('/api/reports/years');
        if (response && response.years) {
          setAvailableYears(response.years);
          if (response.years.length > 0 && !response.years.includes(selectedYear)) {
            setSelectedYear(response.years[0]);
          }
        }
      } catch (error) {
        //console.error('Error fetching available years:', error);
      }
    };
    fetchYears();
  }, [isAuthenticated]);

  // Fetch all performance data
  useEffect(() => {
    if (!isAuthenticated) return;

    const fetchAllData = async () => {
      setLoading(true)
      setError(null)

      try {
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('No authentication token found');
        }

        const payload = JSON.parse(atob(token.split('.')[1]));
        const company_id = payload.company_id;

        let managerId = managerEmployeeId;
        if (!managerId) {
          managerId = await fetchManagerId();
        }


        const yearParam = timePeriod === 'yearly' ? `&year=${selectedYear}` : '';
        const empParam = managerId ? `&employee_id=${managerId}` : '';
        const summaryData = await api.get(`/api/reports/summary?period=${timePeriod}${yearParam}${empParam}`);


        setData({
          goals: summaryData.goals || [],
          reviews: summaryData.reviews || [],
          feedback: summaryData.feedback || [],
          improvements: summaryData.improvements || [],
          succession: summaryData.succession || [],
          employees: summaryData.employees || [],
          training: [],
        })
      } catch (err) {
        //console.error('❌ Error fetching data:', err);
        setError(`Failed to fetch performance data: ${err.message}`);

        // If unauthorized, redirect to login
        if (err.message.includes('Session expired') || err.message.includes('No authentication token')) {
          localStorage.removeItem('token');
          setTimeout(() => {
            window.location.href = '/login';
          }, 3000);
        }
      } finally {
        setLoading(false)
      }
    }

    fetchAllData()
  }, [isAuthenticated, timePeriod, selectedYear, managerEmployeeId])

  // Filter data based on time period
  const filteredData = data;

  // Calculate comprehensive metrics
  const metrics = useMemo(() => {
    const { goals, reviews, feedback, improvements, succession, employees } = filteredData

    // Goals Analysis
    const totalGoals = goals.length
    const completedGoals = goals.filter((g) => g.status === "Completed").length
    const inProgressGoals = goals.filter((g) => g.status === "In Progress").length
    const notStartedGoals = goals.filter((g) => g.status === "Not Started").length

    // Fix for overdue goals calculation
    const overdueGoals = goals.filter((g) => {
      if (g.status === "Completed") return false
      if (!g.dueDate) return false

      const dueDate = new Date(g.dueDate)
      const today = new Date()
      const todayDateOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate())
      const dueDateOnly = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate())

      return dueDateOnly < todayDateOnly
    }).length


    const goalCompletionRate = totalGoals > 0 ? (completedGoals / totalGoals) * 100 : 0

    // Priority Distribution
    const highPriorityGoals = goals.filter((g) => g.priority === "High").length
    const mediumPriorityGoals = goals.filter((g) => g.priority === "Medium").length
    const lowPriorityGoals = goals.filter((g) => g.priority === "Low").length

    // Reviews Analysis
    const totalReviews = reviews.length
    const avgRating =
      reviews.length > 0 ? reviews.reduce((sum, r) => sum + (Number.parseFloat(r.rating) || 0), 0) / reviews.length : 0
    const excellentReviews = reviews.filter((r) => Number.parseFloat(r.rating) >= 4.5).length
    const goodReviews = reviews.filter(
      (r) => Number.parseFloat(r.rating) >= 3.5 && Number.parseFloat(r.rating) < 4.5,
    ).length
    const averageReviews = reviews.filter(
      (r) => Number.parseFloat(r.rating) >= 2.5 && Number.parseFloat(r.rating) < 3.5,
    ).length
    const belowAverageReviews = reviews.filter(
      (r) => Number.parseFloat(r.rating) >= 1.5 && Number.parseFloat(r.rating) < 2.5,
    ).length
    const poorReviews = reviews.filter((r) => Number.parseFloat(r.rating) < 1.5).length

    // Feedback Analysis
    const totalFeedback = feedback.length
    const positiveFeedback = feedback.filter((f) => f.type === "positive" || Number.parseFloat(f.rating) >= 4).length
    const constructiveFeedback = feedback.filter(
      (f) => f.type === "constructive" || (Number.parseFloat(f.rating) >= 2 && Number.parseFloat(f.rating) < 4),
    ).length
    const negativeFeedback = feedback.filter((f) => f.type === "negative" || Number.parseFloat(f.rating) < 2).length

    // Employee Engagement
    const totalEmployees = employees.length
    const activeEmployees = employees.filter(
      (emp) =>
        goals.some((g) => g.employee_id === emp.employee_id) || reviews.some((r) => r.employee_id === emp.employee_id),
    ).length
    const engagementRate = totalEmployees > 0 ? (activeEmployees / totalEmployees) * 100 : 0

    // Improvement Plans
    const totalImprovements = improvements.length
    const completedImprovements = improvements.filter((i) => i.status === "Completed").length
    const activeImprovements = improvements.filter((i) => i.status === "Active" || i.status === "In Progress").length

    // Succession Planning
    const totalSuccessionPlans = succession.length

    const readySuccessors = succession.filter((s) => {
      return s.readiness === "Ready" || s.readinessLevel === "Ready" || s.status === "Ready"
    }).length


    return {
      // Goals
      totalGoals,
      completedGoals,
      inProgressGoals,
      notStartedGoals,
      overdueGoals,
      goalCompletionRate,
      highPriorityGoals,
      mediumPriorityGoals,
      lowPriorityGoals,

      // Reviews
      totalReviews,
      avgRating,
      excellentReviews,
      goodReviews,
      averageReviews,
      belowAverageReviews,
      poorReviews,

      // Feedback
      totalFeedback,
      positiveFeedback,
      constructiveFeedback,
      negativeFeedback,

      // Employees
      totalEmployees,
      activeEmployees,
      engagementRate,

      // Development
      totalImprovements,
      completedImprovements,
      activeImprovements,

      // Succession
      totalSuccessionPlans,
      readySuccessors,
    }
  }, [filteredData])

  // Chart data generation
  const chartData = useMemo(() => {
    const goalStatusData = [
      {
        name: "Completed",
        value: metrics.completedGoals,
        color: "#4caf50",
        employees: filteredData.goals.filter(g => g.status === "Completed").map(g => ({
          ...g,
          employee: filteredData.employees.find(emp => emp.employee_id === g.employee_id)
        }))
      },
      {
        name: "In Progress",
        value: metrics.inProgressGoals,
        color: "#ff9800",
        employees: filteredData.goals.filter(g => g.status === "In Progress").map(g => ({
          ...g,
          employee: filteredData.employees.find(emp => emp.employee_id === g.employee_id)
        }))
      },
      {
        name: "Not Started",
        value: metrics.notStartedGoals,
        color: "#2196f3",
        employees: filteredData.goals.filter(g => g.status === "Not Started").map(g => ({
          ...g,
          employee: filteredData.employees.find(emp => emp.employee_id === g.employee_id)
        }))
      },
      {
        name: "Overdue",
        value: metrics.overdueGoals,
        color: "#f44336",
        employees: filteredData.goals.filter(g => {
          if (g.status === "Completed") return false
          if (!g.dueDate) return false
          const dueDate = new Date(g.dueDate)
          const today = new Date()
          const todayDateOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate())
          const dueDateOnly = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate())
          return dueDateOnly < todayDateOnly
        }).map(g => ({
          ...g,
          employee: filteredData.employees.find(emp => emp.employee_id === g.employee_id)
        }))
      },
    ]


    const priorityData = [
      { name: "High", value: metrics.highPriorityGoals, color: "#f44336" },
      { name: "Medium", value: metrics.mediumPriorityGoals, color: "#ff9800" },
      { name: "Low", value: metrics.lowPriorityGoals, color: "#4caf50" },
    ]

    const ratingData = [
      { name: "Excellent (4.5+)", value: metrics.excellentReviews, color: "#4caf50" },
      { name: "Good (3.5-4.4)", value: metrics.goodReviews, color: "#8bc34a" },
      { name: "Average (2.5-3.4)", value: metrics.averageReviews, color: "#ff9800" },
      { name: "Below Average (1.5-2.4)", value: metrics.belowAverageReviews, color: "#ff5722" },
      { name: "Poor (<1.5)", value: metrics.poorReviews, color: "#f44336" },
    ]

    const performanceTrend = []
    for (let i = 5; i >= 0; i--) {
      const month = new Date()
      month.setMonth(month.getMonth() - i)
      const monthStr = month.toLocaleDateString("en-US", { month: "short" })

      const monthGoals = filteredData.goals.filter((g) => {
        const goalDate = new Date(g.dueDate || g.createdAt)
        return goalDate.getMonth() === month.getMonth() && goalDate.getFullYear() === month.getFullYear()
      })

      const monthReviews = filteredData.reviews.filter((r) => {
        const reviewDate = new Date(r.createdAt || r.date)
        return reviewDate.getMonth() === month.getMonth() && reviewDate.getFullYear() === month.getFullYear()
      })

      performanceTrend.push({
        month: monthStr,
        goals: monthGoals.length,
        completed: monthGoals.filter((g) => g.status === "Completed").length,
        reviews: monthReviews.length,
        avgRating:
          monthReviews.length > 0
            ? monthReviews.reduce((sum, r) => sum + (Number.parseFloat(r.rating) || 0), 0) / monthReviews.length
            : 0,
      })
    }

    return { goalStatusData, priorityData, ratingData, performanceTrend }
  }, [metrics, filteredData])

  const handlePieClick = (data, index) => {
    if (data && data.employees) {
      setSelectedGoalStatus({
        status: data.name,
        employees: data.employees,
        color: data.color
      })
      setStatusModalOpen(true)
    }
  }

  // Top performers calculation
  const topPerformers = useMemo(() => {
    const { goals, reviews, employees } = filteredData

    return employees
      .map((emp) => {
        const empGoals = goals.filter((g) => g.employee_id === emp.employee_id)
        const empReviews = reviews.filter((r) => r.employee_id === emp.employee_id)

        const completedGoals = empGoals.filter((g) => g.status === "Completed").length
        const totalGoals = empGoals.length
        const completionRate = totalGoals > 0 ? (completedGoals / totalGoals) * 100 : 0
        const avgRating =
          empReviews.length > 0
            ? empReviews.reduce((sum, r) => sum + (Number.parseFloat(r.rating) || 0), 0) / empReviews.length
            : 0

        return {
          ...emp,
          completedGoals,
          totalGoals,
          completionRate,
          avgRating,
          reviewCount: empReviews.length,
          score: completionRate * 0.6 + avgRating * 20 * 0.4,
        }
      })
      .filter((emp) => emp.totalGoals > 0 || emp.reviewCount > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 10)
  }, [filteredData])

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
  }

  const handleTimePeriodChange = (event) => {
    setTimePeriod(event.target.value)
  }

  // ✅ Updated PDF export with authentication
  const handleExportPDF = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('No authentication token found. Please log in.');
        return;
      }

      const company_id = api.getCompanyId();
      if (!company_id) {
        alert('Company ID not found. Please log in again.');
        return;
      }

      const empParam = managerEmployeeId ? `&employee_id=${managerEmployeeId}` : '';
      const response = await fetch(
        `${API_BASE_URL}/api/reports/export/pdf?timePeriod=${timePeriod}&company_id=${company_id}${empParam}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/pdf',
          },
          credentials: 'include', // ✅ Include credentials
        }
      )

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('token');
          alert('Session expired. Please log in again.');
          window.location.href = '/login';
          return;
        }
        throw new Error(`Failed to generate PDF: ${response.status} ${response.statusText}`)
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `performance-report-${Date.now()}.pdf`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error) {
      //console.error('❌ Error exporting PDF:', error)
      alert('Failed to export PDF: ' + error.message)
    }
  }

  // ✅ Updated DOC export with authentication
  const handleExportDOC = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('No authentication token found. Please log in.');
        return;
      }

      const company_id = api.getCompanyId();
      if (!company_id) {
        alert('Company ID not found. Please log in again.');
        return;
      }

      const empParam = managerEmployeeId ? `&employee_id=${managerEmployeeId}` : '';
      const response = await fetch(
        `${API_BASE_URL}/api/reports/export/doc?timePeriod=${timePeriod}&company_id=${company_id}${empParam}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          },
          credentials: 'include', // ✅ Include credentials
        }
      )

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('token');
          alert('Session expired. Please log in again.');
          window.location.href = '/login';
          return;
        }
        throw new Error(`Failed to generate DOC: ${response.status} ${response.statusText}`)
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `performance-report-${Date.now()}.docx`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error) {
      //console.error('❌ Error exporting DOC:', error)
      alert('Failed to export DOC: ' + error.message)
    }
  }

  if (loading) {
    return (
      <Container
        maxWidth="xl"
        sx={{ mt: 4, display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}
      >
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading Performance Report...
          </Typography>
        </Box>
      </Container>
    )
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <IconButton onClick={() => window.location.reload()} size="large">
            <Refresh />
          </IconButton>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Click to retry
          </Typography>
        </Box>
      </Container>
    )
  }

  // StatusModal component
  const StatusModal = () => {
    const getLatestProgressNote = (goalData) => {
      if (goalData.progressUpdates && goalData.progressUpdates.length > 0) {
        const sortedUpdates = goalData.progressUpdates.sort((a, b) => new Date(b.date) - new Date(a.date));
        return sortedUpdates[0].note || "No details provided";
      }
      return goalData.notes || goalData.reason || goalData.statusNotes || "No updates provided";
    };

    return (
      <Dialog
        open={statusModalOpen}
        onClose={() => setStatusModalOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={selectedGoalStatus?.status}
            sx={{ bgcolor: selectedGoalStatus?.color, color: 'white' }}
          />
          Goals Details ({selectedGoalStatus?.employees?.length || 0} employees)
        </DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>Goal Title</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Progress</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell width="300px">Latest Update</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedGoalStatus?.employees?.map((goalData, index) => (
                  <TableRow key={goalData.goal_id || index}>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Avatar sx={{ width: 32, height: 32, mr: 2, bgcolor: "primary.light" }}>
                          {goalData.employee ?
                            `${goalData.employee.firstName?.[0]}${goalData.employee.lastName?.[0]}` :
                            "N/A"
                          }
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {goalData.employee ?
                              `${goalData.employee.firstName} ${goalData.employee.lastName}` :
                              "Unknown Employee"
                            }
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {goalData.employee?.position || "Position N/A"}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {goalData.title || goalData.description || "Goal Title"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {goalData.category || "General"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={goalData.priority || "Medium"}
                        size="small"
                        color={
                          goalData.priority === "High"
                            ? "error"
                            : goalData.priority === "Medium"
                              ? "warning"
                              : "success"
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">
                          {goalData.progress || 0}%
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={goalData.progress || 0}
                          sx={{
                            width: 60,
                            height: 6,
                            borderRadius: 3,
                          }}
                          color={goalData.progress === 100 ? "success" : "primary"}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {goalData.dueDate ? new Date(goalData.dueDate).toLocaleDateString() : "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ maxWidth: 300 }}>
                        <Typography variant="body2" sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {getLatestProgressNote(goalData)}
                        </Typography>

                        {goalData.progressUpdates && goalData.progressUpdates.length > 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            Updated: {new Date(goalData.progressUpdates.sort((a, b) => new Date(b.date) - new Date(a.date))[0].date).toLocaleDateString()}
                          </Typography>
                        )}

                        {goalData.progressUpdates && goalData.progressUpdates.length > 1 && (
                          <Chip
                            label={`${goalData.progressUpdates.length} updates`}
                            size="small"
                            variant="outlined"
                            sx={{ mt: 0.5, fontSize: '0.7rem', height: 20 }}
                          />
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    )
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4, border: "2px solid", borderColor: "grey.300", boxShadow: 4 }}>
      {/* Header */}
      <Paper elevation={1} sx={{ p: 3, mb: 3, mt: 3, border: "1px solid", borderColor: "grey.300", boxShadow: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Performance Management Report
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Time Period</InputLabel>
                <Select value={timePeriod} onChange={handleTimePeriodChange} label="Time Period">
                  <MenuItem value="all">All Time</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="quarterly">Quarterly</MenuItem>
                  <MenuItem value="yearly">Yearly</MenuItem>
                </Select>
              </FormControl>

              {timePeriod === "yearly" && (
                <FormControl size="small" sx={{ minWidth: 110 }}>
                  <InputLabel>Year</InputLabel>
                  <Select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                    label="Year"
                  >
                    {availableYears.map((yr) => (
                      <MenuItem key={yr} value={yr}>{yr}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center", pl: 65 }}>
              <Tooltip title="Export to PDF">
                <Button
                  onClick={handleExportPDF}
                  variant="outlined"
                  startIcon={<PictureAsPdfIcon />}
                  sx={{
                    padding: "10px 10px",
                    fontSize: "0.9rem",
                    textTransform: "none",
                    borderRadius: "8px",
                    borderWidth: 2,
                  }}
                >
                  Export to PDF
                </Button>
              </Tooltip>

              <Tooltip title="Export to DOC">
                <Button
                  onClick={handleExportDOC}
                  variant="outlined"
                  startIcon={<Description />}
                  sx={{
                    padding: "10px 10px",
                    fontSize: "0.9rem",
                    textTransform: "none",
                    borderRadius: "8px",
                    borderWidth: 2,
                  }}
                >
                  Export to DOC
                </Button>
              </Tooltip>

              <Tooltip title="Refresh Data">
                <IconButton onClick={() => window.location.reload()}>
                  <Refresh />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* KPI Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="primary">
                    {metrics.totalGoals}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Goals
                  </Typography>
                </Box>
                <Assignment sx={{ fontSize: 40, color: "primary.main", opacity: 0.7 }} />
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Completion Rate: {metrics.goalCompletionRate.toFixed(1)}%
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={metrics.goalCompletionRate}
                  sx={{ mt: 1, height: 6, borderRadius: 3 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {metrics.completedGoals}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Completed Goals
                  </Typography>
                </Box>
                <CheckCircle sx={{ fontSize: 40, color: "success.main", opacity: 0.7 }} />
              </Box>
              <Box sx={{ mt: 2 }}>
                <Chip
                  label={`${metrics.overdueGoals} Overdue`}
                  size="small"
                  color={metrics.overdueGoals > 0 ? "error" : "default"}
                  variant="outlined"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="warning.main">
                    {metrics.avgRating.toFixed(1)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Average Rating
                  </Typography>
                </Box>
                <Star sx={{ fontSize: 40, color: "warning.main", opacity: 0.7 }} />
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  From {metrics.totalReviews} Reviews
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="info.main">
                    {metrics.engagementRate.toFixed(1)}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Employee Engagement
                  </Typography>
                </Box>
                <People sx={{ fontSize: 40, color: "info.main", opacity: 0.7 }} />
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  {metrics.activeEmployees} of {metrics.totalEmployees} Employees
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabbed Content - Keep the rest of your tabs as they were */}
      {/* ... (all the TabPanel content remains the same) ... */}
      <Paper elevation={2} sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="performance report tabs"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<Dashboard />} label="Overview" />
          <Tab icon={<Assignment />} label="Goals" />
          <Tab icon={<Assessment />} label="Reviews" />
          <Tab icon={<Feedback />} label="Feedback" />
          <Tab icon={<Timeline />} label="Improvement" />
        </Tabs>

        {/* Overview Tab */}
        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={8}>
              <Card elevation={1}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
                    <ShowChart sx={{ mr: 1 }} />
                    Performance Overview (Last 6 Months)
                  </Typography>
                  <Box sx={{ height: 350 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData.performanceTrend}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <RechartsTooltip />
                        <Legend />
                        <Line type="monotone" dataKey="goals" stroke="#1976d2" name="Total Goals" strokeWidth={2} />
                        <Line type="monotone" dataKey="completed" stroke="#4caf50" name="Completed" strokeWidth={2} />
                        <Line type="monotone" dataKey="reviews" stroke="#ff9800" name="Reviews" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} lg={4}>
              <Card elevation={1}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
                    <EmojiEvents sx={{ mr: 1 }} />
                    Key Metrics
                  </Typography>
                  <Stack spacing={2}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography variant="body2">High Priority Goals</Typography>
                      <Chip label={metrics.highPriorityGoals} color="error" size="small" />
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography variant="body2">Positive Feedback</Typography>
                      <Chip
                        label={`${((metrics.positiveFeedback / Math.max(metrics.totalFeedback, 1)) * 100).toFixed(0)}%`}
                        color="success"
                        size="small"
                      />
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography variant="body2">Active Improvements</Typography>
                      <Chip label={metrics.activeImprovements} color="info" size="small" />
                    </Box>

                    <Divider />
                    <Box sx={{ textAlign: "center", pt: 2 }}>
                      <Typography variant="h5" fontWeight="bold" color="primary">
                        {((metrics.completedGoals / Math.max(metrics.totalGoals, 1)) * 100).toFixed(0)}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Overall Completion Rate
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Goals Tab */}
        <TabPanel value={activeTab} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card elevation={1}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
                    <PieChart sx={{ mr: 1 }} />
                    Goal Status Distribution
                  </Typography>
                  <Box sx={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsPieChart>
                        <Pie
                          data={chartData.goalStatusData}
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                          label={({ name, value }) => `${name}: ${value}`}
                          onClick={handlePieClick}
                          style={{ cursor: 'pointer' }}
                        >
                          {chartData.goalStatusData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <RechartsTooltip />
                      </RechartsPieChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card elevation={1}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
                    <Speed sx={{ mr: 1 }} />
                    Priority Distribution
                  </Typography>
                  <Box sx={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsBarChart data={chartData.priorityData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <RechartsTooltip />
                        <Legend />
                        <Bar dataKey="value">
                          {chartData.priorityData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Bar>
                      </RechartsBarChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card elevation={1}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Recent Goals Overview
                  </Typography>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Employee</TableCell>
                          <TableCell>Goal</TableCell>
                          <TableCell>Priority</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Due Date</TableCell>
                          <TableCell>Progress</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredData.goals.slice(0, 10).map((goal, index) => {
                          const employee = filteredData.employees.find((emp) => emp.employee_id === goal.employee_id)
                          return (
                            <TableRow key={goal.goal_id || index}>
                              <TableCell>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                  <Avatar sx={{ width: 32, height: 32, mr: 2, bgcolor: "primary.light" }}>
                                    {employee ? `${employee.firstName?.[0]}${employee.lastName?.[0]}` : "N/A"}
                                  </Avatar>
                                  <Box>
                                    <Typography variant="body2" fontWeight="medium">
                                      {employee ? `${employee.firstName} ${employee.lastName}` : "Unknown Employee"}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {employee?.position || "Position N/A"}
                                    </Typography>
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ maxWidth: 200 }}>
                                  {goal.title || goal.description || "Goal Title"}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                                  {goal.category || "General"}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={goal.priority || "Medium"}
                                  size="small"
                                  color={
                                    goal.priority === "High"
                                      ? "error"
                                      : goal.priority === "Medium"
                                        ? "warning"
                                        : "success"
                                  }
                                />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={goal.status || "Not Started"}
                                  size="small"
                                  variant="outlined"
                                  color={
                                    goal.status === "Completed"
                                      ? "success"
                                      : goal.status === "In Progress"
                                        ? "warning"
                                        : "default"
                                  }
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {goal.dueDate ? new Date(goal.dueDate).toLocaleDateString() : "N/A"}
                                </Typography>
                                {goal.dueDate && new Date(goal.dueDate) < new Date() && goal.status !== "Completed" && (
                                  <Typography variant="caption" color="error" sx={{ display: "block" }}>
                                    Overdue
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <LinearProgress
                                    variant="determinate"
                                    value={
                                      goal.progress ||
                                      (goal.status === "Completed" ? 100 : goal.status === "In Progress" ? 50 : 0)
                                    }
                                    sx={{ flexGrow: 1, height: 6, borderRadius: 3 }}
                                  />
                                  <Typography variant="caption">
                                    {goal.progress ||
                                      (goal.status === "Completed" ? 100 : goal.status === "In Progress" ? 50 : 0)}
                                    %
                                  </Typography>
                                </Box>
                              </TableCell>
                            </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Reviews Tab */}
        <TabPanel value={activeTab} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card elevation={1}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
                    <Assessment sx={{ mr: 1 }} />
                    Performance Rating Distribution
                  </Typography>
                  <Box sx={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsBarChart data={chartData.ratingData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <RechartsTooltip />
                        <Bar dataKey="value" fill="#8884d8">
                          {chartData.ratingData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Bar>
                      </RechartsBarChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card elevation={1}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
                    <EmojiEvents sx={{ mr: 1 }} />
                    Top Performers
                  </Typography>
                  <List>
                    {topPerformers.slice(0, 5).map((employee, index) => (
                      <ListItem key={employee.employee_id || index} divider={index < 4}>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: index < 3 ? "warning.main" : "primary.main" }}>{index + 1}</Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={`${employee.firstName || "N/A"} ${employee.lastName || ""}`}
                          secondary={
                            <Box>
                              <Typography variant="caption" component="div">
                                {employee.position || "Position N/A"}
                              </Typography>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                                <Rating value={employee.avgRating} precision={0.1} size="small" readOnly />
                                <Typography variant="caption">({employee.avgRating.toFixed(1)})</Typography>
                              </Box>
                              <Typography variant="caption" color="text.secondary">
                                {employee.completedGoals}/{employee.totalGoals} goals completed
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card elevation={1}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Recent Performance Reviews
                  </Typography>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Employee</TableCell>
                          <TableCell>Review Date</TableCell>
                          <TableCell>Rating</TableCell>
                          <TableCell>Reviewer</TableCell>
                          <TableCell>Comments</TableCell>
                          <TableCell>Areas for Improvement</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredData.reviews.slice(0, 10).map((review, index) => {
                          const employee = filteredData.employees.find((emp) => emp.employee_id === review.employee_id)
                          return (
                            <TableRow key={review.review_id || index}>
                              <TableCell>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                  <Avatar sx={{ width: 32, height: 32, mr: 2, bgcolor: "primary.light" }}>
                                    {employee ? `${employee.firstName?.[0]}${employee.lastName?.[0]}` : "N/A"}
                                  </Avatar>
                                  <Typography variant="body2">
                                    {employee ? `${employee.firstName} ${employee.lastName}` : "Unknown Employee"}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {review.reviewDate || review.createdAt
                                    ? new Date(review.reviewDate || review.createdAt).toLocaleDateString()
                                    : "N/A"}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <Rating
                                    value={Number.parseFloat(review.rating) || 0}
                                    precision={0.1}
                                    size="small"
                                    readOnly
                                  />
                                  <Typography variant="caption">
                                    ({(Number.parseFloat(review.rating) || 0).toFixed(1)})
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {review.reviewerName || review.reviewer || "Manager"}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ maxWidth: 200 }}>
                                  {review.comments || review.feedback || "No comments provided"}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ maxWidth: 200 }}>
                                  {review.areasForImprovement || review.improvements || "None specified"}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Feedback Tab */}
        <TabPanel value={activeTab} index={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card elevation={1} sx={{ bgcolor: "success.light", color: "success.contrastText" }}>
                <CardContent>
                  <Typography variant="h4" fontWeight="bold">
                    {metrics.positiveFeedback}
                  </Typography>
                  <Typography variant="body2">Positive Feedback</Typography>
                  <Typography variant="caption">
                    {metrics.totalFeedback > 0
                      ? ((metrics.positiveFeedback / metrics.totalFeedback) * 100).toFixed(1)
                      : 0}
                    % of total
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card elevation={1} sx={{ bgcolor: "warning.light", color: "warning.contrastText" }}>
                <CardContent>
                  <Typography variant="h4" fontWeight="bold">
                    {metrics.constructiveFeedback}
                  </Typography>
                  <Typography variant="body2">Constructive Feedback</Typography>
                  <Typography variant="caption">
                    {metrics.totalFeedback > 0
                      ? ((metrics.constructiveFeedback / metrics.totalFeedback) * 100).toFixed(1)
                      : 0}
                    % of total
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card elevation={1} sx={{ bgcolor: "error.light", color: "error.contrastText" }}>
                <CardContent>
                  <Typography variant="h4" fontWeight="bold">
                    {metrics.negativeFeedback}
                  </Typography>
                  <Typography variant="body2">Negative Feedback</Typography>
                  <Typography variant="caption">
                    {metrics.totalFeedback > 0
                      ? ((metrics.negativeFeedback / metrics.totalFeedback) * 100).toFixed(1)
                      : 0}
                    % of total
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card elevation={1}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Recent Feedback
                  </Typography>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Employee</TableCell>
                          <TableCell>Feedback Type</TableCell>
                          <TableCell>Rating</TableCell>
                          <TableCell>Feedback</TableCell>
                          <TableCell>Feedback Giver</TableCell>
                          <TableCell>Date</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredData.feedback.slice(0, 10).map((feedback, index) => {
                          const employee = filteredData.employees.find(
                            (emp) => emp.employee_id === feedback.employee_id,
                          )
                          return (
                            <TableRow key={feedback.feedback_id || index}>
                              <TableCell>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                  <Avatar sx={{ width: 32, height: 32, mr: 2, bgcolor: "primary.light" }}>
                                    {employee ? `${employee.firstName?.[0]}${employee.lastName?.[0]}` : "N/A"}
                                  </Avatar>
                                  <Typography variant="body2">
                                    {employee ? `${employee.firstName} ${employee.lastName}` : "Unknown Employee"}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={feedback.type || "General"}
                                  size="small"
                                  color={
                                    feedback.type === "positive"
                                      ? "success"
                                      : feedback.type === "constructive"
                                        ? "warning"
                                        : feedback.type === "negative"
                                          ? "error"
                                          : "default"
                                  }
                                />
                              </TableCell>
                              <TableCell>
                                {feedback.rating && (
                                  <Rating
                                    value={Number.parseFloat(feedback.rating)}
                                    precision={0.1}
                                    size="small"
                                    readOnly
                                  />
                                )}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ maxWidth: 300 }}>
                                  {feedback.comment || feedback.feedback || "No feedback provided"}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {feedback.feedbackGiver || feedback.reviewer || "Anonymous"}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {feedback.date || feedback.createdAt
                                    ? new Date(feedback.date || feedback.createdAt).toLocaleDateString()
                                    : "N/A"}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Improvement Tab */}
        <TabPanel value={activeTab} index={4}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card elevation={1}>
                <CardContent>
                  <Typography variant="h4" fontWeight="bold" color="primary">
                    {metrics.totalImprovements}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Improvement Plans
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={
                      metrics.totalImprovements > 0
                        ? (metrics.completedImprovements / metrics.totalImprovements) * 100
                        : 0
                    }
                    sx={{ mt: 2, height: 6, borderRadius: 3 }}
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card elevation={1}>
                <CardContent>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {metrics.completedImprovements}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Completed Plans
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                    {metrics.totalImprovements > 0
                      ? ((metrics.completedImprovements / metrics.totalImprovements) * 100).toFixed(1)
                      : 0}
                    % completion rate
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card elevation={1}>
                <CardContent>
                  <Typography variant="h4" fontWeight="bold" color="warning.main">
                    {metrics.activeImprovements}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Plans
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                    Currently in progress
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card elevation={1}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Improvement Plans
                  </Typography>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Employee</TableCell>
                          <TableCell>Plan Title</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Start Date</TableCell>
                          <TableCell>Target Date</TableCell>
                          <TableCell>Progress</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredData.improvements.slice(0, 10).map((plan, index) => {
                          const employee = filteredData.employees.find((emp) => emp.employee_id === plan.employee_id)
                          return (
                            <TableRow key={plan.plan_id || index}>
                              <TableCell>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                  <Avatar sx={{ width: 32, height: 32, mr: 2, bgcolor: "primary.light" }}>
                                    {employee ? `${employee.firstName?.[0]}${employee.lastName?.[0]}` : "N/A"}
                                  </Avatar>
                                  <Typography variant="body2">
                                    {employee ? `${employee.firstName} ${employee.lastName}` : "Unknown Employee"}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {plan.title || plan.planTitle || "Development Plan"}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={plan.status || "Active"}
                                  size="small"
                                  color={
                                    plan.status === "Completed"
                                      ? "success"
                                      : plan.status === "Active" || plan.status === "In Progress"
                                        ? "warning"
                                        : "default"
                                  }
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {plan.startDate ? new Date(plan.startDate).toLocaleDateString() : "N/A"}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {plan.targetDate || plan.endDate
                                    ? new Date(plan.targetDate || plan.endDate).toLocaleDateString()
                                    : "N/A"}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <LinearProgress
                                    variant="determinate"
                                    value={
                                      plan.progress ||
                                      (plan.status === "Completed" ? 100 : plan.status === "Active" ? 50 : 0)
                                    }
                                    sx={{ flexGrow: 1, height: 6, borderRadius: 3 }}
                                  />
                                  <Typography variant="caption">
                                    {plan.progress ||
                                      (plan.status === "Completed" ? 100 : plan.status === "Active" ? 50 : 0)}
                                    %
                                  </Typography>
                                </Box>
                              </TableCell>
                            </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>
      <StatusModal />
    </Container>
  )
}

export default PerformanceReportDashboard